import React, { useState } from 'react'
import { Layout, Menu, Avatar, Dropdown, Button, Drawer, Grid, theme } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  CarOutlined,
  TruckOutlined,
  CalculatorOutlined,
  DollarOutlined,
  CrownOutlined,
  ToolOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons'
import { useAuthStore } from '../../stores/authStore'
import type { MenuProps } from 'antd'

const { Header, Sider, Content } = Layout

interface DashboardLayoutProps {
  children: React.ReactNode
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { token: { colorBgContainer } } = theme.useToken()
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md

  const getMenuItems = (): MenuProps['items'] => {
    if (!user) return []

    const roleMenus: Record<string, MenuProps['items']> = {
      A: [
        {
          key: '/boss',
          icon: <CrownOutlined />,
          label: '总管理员',
          onClick: () => navigate('/boss')
        },
        {
          key: '/vehicles',
          icon: <CarOutlined />,
          label: '车辆管理',
          onClick: () => navigate('/vehicles')
        },
        {
          key: '/vehicle-manager',
          icon: <TruckOutlined />,
          label: '车辆收租管理',
          onClick: () => navigate('/vehicle-manager')
        },
        {
          key: '/accountant',
          icon: <CalculatorOutlined />,
          label: '会计员',
          onClick: () => navigate('/accountant')
        },
        {
          key: '/cashier',
          icon: <DollarOutlined />,
          label: '出纳员',
          onClick: () => navigate('/cashier')
        },
        {
          key: '/parts',
          icon: <ToolOutlined />,
          label: '配置端',
          onClick: () => navigate('/parts')
        }
      ],
      B: [
        {
          key: '/vehicles',
          icon: <CarOutlined />,
          label: '车辆管理',
          onClick: () => navigate('/vehicles')
        },
        {
          key: '/vehicle-manager',
          icon: <TruckOutlined />,
          label: '车辆收租管理',
          onClick: () => navigate('/vehicle-manager')
        },
        {
          key: '/accountant',
          icon: <CalculatorOutlined />,
          label: '会计员工作台',
          onClick: () => navigate('/accountant')
        }
      ],
      C: [
        {
          key: '/vehicles',
          icon: <CarOutlined />,
          label: '车辆管理',
          onClick: () => navigate('/vehicles')
        },
        {
          key: '/vehicle-manager',
          icon: <TruckOutlined />,
          label: '车辆收租管理',
          onClick: () => navigate('/vehicle-manager')
        }
      ],
      D: [
        {
          key: '/parts',
          icon: <ToolOutlined />,
          label: '配置端',
          onClick: () => navigate('/parts')
        }
      ]
    }

    return roleMenus[user.role] || []
  }

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息'
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => {
        logout()
        navigate('/login')
      }
    }
  ]

  return (
    <Layout style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          style={{
            background: 'var(--surface)',
            borderRight: '1px solid var(--line)'
          }}
        >
          <div style={{
            height: 64,
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            borderBottom: '1px solid var(--line)'
          }}>
            {!collapsed && (
              <h2 style={{ margin: 0, color: 'var(--accent)' }}>JXC车辆管理系统</h2>
            )}
            {collapsed && (
              <CarOutlined style={{ fontSize: 24, color: 'var(--accent)' }} />
            )}
          </div>

          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={getMenuItems()}
            style={{ borderRight: 0 }}
          />
        </Sider>
      )}

      {isMobile && (
        <Drawer
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          placement="left"
          width={260}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{
            height: 64,
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid var(--line)'
          }}>
            <h2 style={{ margin: 0, color: 'var(--accent)' }}>JXC车辆管理系统</h2>
          </div>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={getMenuItems()}
            onClick={() => setMobileMenuOpen(false)}
            style={{ borderRight: 0 }}
          />
        </Drawer>
      )}

      <Layout>
        <Header style={{
          padding: isMobile ? '0 12px' : '0 16px',
          background: 'var(--surface)',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Button
            type="text"
            icon={
              isMobile
                ? <MenuUnfoldOutlined />
                : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)
            }
            onClick={() => {
              if (isMobile) {
                setMobileMenuOpen(true)
              } else {
                setCollapsed(!collapsed)
              }
            }}
            style={{ fontSize: '16px', width: 48, height: 48 }}
          />

          {user && (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '6px 10px',
                borderRadius: '6px',
                transition: 'background-color 0.2s',
                color: 'var(--text)'
              }}>
                <Avatar
                  size="small"
                  style={{ marginRight: 8, backgroundColor: 'var(--accent)' }}
                >
                  {user.avatarUrl || user.nickName.charAt(0)}
                </Avatar>
                {!isMobile && <span>{user.nickName}</span>}
              </div>
            </Dropdown>
          )}
        </Header>

        <Content style={{
          margin: isMobile ? '12px' : '16px',
          padding: isMobile ? '12px' : '24px',
          background: 'var(--surface-alt)',
          borderRadius: '12px',
          minHeight: isMobile ? 'calc(100vh - 88px)' : 'calc(100vh - 112px)',
          overflow: 'auto'
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  )
}

export default DashboardLayout

